import React, { useEffect, useState } from "react";
import { Link, Navigate, useSearchParams } from "react-router-dom";
import { api, formatINR } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { logDev } from "@/lib/logger";
import SEO from "@/components/SEO";
import PropertyCard from "@/components/PropertyCard";
import {
  LayoutDashboard, Heart, ClipboardList, User as UserIcon,
  CreditCard, MessageSquare, Sparkles, CheckCircle2, X, Lock,
} from "lucide-react";

const BUYER_PLANS = [
  { title: "Free", price: "₹0", period: "Current plan", features: ["Browse all listings", "Save favourites", "Post requirements", "Standard support"], active: true },
  { title: "Priority Buyer", price: "Coming Soon", period: "Monthly", features: ["Dedicated Tharlo advisor", "Early access to new listings", "Site-visit scheduling", "Weekend availability"], disabled: true },
  { title: "Concierge", price: "Coming Soon", period: "Per deal", features: ["End-to-end deal handling", "Legal + Loan support included", "Negotiation assistance", "On-ground visits"], disabled: true },
];

const SIDEBAR = [
  { key: "dashboard", label: "Dashboard", icon: LayoutDashboard },
  { key: "saved", label: "Saved Properties", icon: Heart },
  { key: "requirements", label: "My Requirements", icon: ClipboardList },
  { key: "unlocks", label: "Unlock Requests", icon: Lock },
  { key: "profile", label: "Profile", icon: UserIcon },
  { key: "subscription", label: "Buyer Plans", icon: CreditCard },
  { key: "support", label: "Support", icon: MessageSquare },
];

export default function BuyerDashboard() {
  const { user, loading, openRequirement } = useAuth();
  const [params, setParams] = useSearchParams();
  const activeSection = params.get("s") || "dashboard";
  const setSection = (k) => { const np = new URLSearchParams(params); np.set("s", k); setParams(np); };

  if (loading) return <div className="container-tharlo py-20">Loading...</div>;
  if (!user) return <Navigate to="/" replace />;
  // Seller-only users should not be here
  if (user.user_type === "seller") return <Navigate to="/broker" replace />;

  return (
    <div className="container-tharlo py-8" data-testid="buyer-dashboard">
      <SEO title="My Dashboard" description="Your saved properties and requirements on Tharlo." noindex />

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        <aside className="lg:col-span-3">
          <div className="bg-white border border-border p-5 sticky top-24">
            <div className="pb-4 border-b border-border">
              <div className="text-xs uppercase tracking-widest text-muted-foreground">Signed in as</div>
              <div className="font-display text-lg text-ink mt-1 truncate">{user.name}</div>
              <div className="text-[10px] uppercase tracking-widest text-gold mt-1">
                {user.user_type === "both" ? "Buyer + Seller" : "Buyer"}
              </div>
            </div>
            <nav className="mt-4 space-y-1">
              {SIDEBAR.map((s) => {
                const Icon = s.icon;
                const active = activeSection === s.key;
                return (
                  <button
                    key={s.key}
                    onClick={() => setSection(s.key)}
                    data-testid={`buyer-sidebar-${s.key}`}
                    className={`w-full flex items-center gap-2.5 px-3 py-2.5 text-sm text-left transition-colors ${active ? "bg-ink text-white" : "text-foreground/80 hover:bg-muted"}`}
                  >
                    <Icon className="w-4 h-4" /> {s.label}
                  </button>
                );
              })}
            </nav>
            {user.user_type === "both" && (
              <Link to="/broker" data-testid="buyer-goto-seller" className="mt-4 block text-xs text-gold hover:underline text-center">
                Switch to Seller Dashboard →
              </Link>
            )}
          </div>
        </aside>

        <main className="lg:col-span-9">
          {activeSection === "dashboard" && <BuyerHome onNav={setSection} openRequirement={openRequirement} />}
          {activeSection === "saved" && <SavedProperties />}
          {activeSection === "requirements" && <MyRequirements openPost={openRequirement} />}
          {activeSection === "unlocks" && <MyUnlockRequests />}
          {activeSection === "profile" && <BuyerProfile />}
          {activeSection === "subscription" && <BuyerSubscription />}
          {activeSection === "support" && <SupportSection />}
        </main>
      </div>
    </div>
  );
}

/* ============ Buyer Home ============ */
function BuyerHome({ onNav, openRequirement }) {
  const [likesCount, setLikesCount] = useState(0);
  const [reqCount, setReqCount] = useState(0);
  const [cbCount, setCbCount] = useState(0);

  useEffect(() => {
    api.get("/my/likes").then((r) => {
      setLikesCount(r.data.items.length);
      setCbCount((r.data.callback_property_ids || []).length);
    });
    api.get("/my/requirements").then((r) => setReqCount(r.data.items.length));
  }, []);

  return (
    <div className="space-y-8">
      <div>
        <div className="overline mb-2">Buyer Dashboard</div>
        <h1 className="font-display text-4xl text-ink tracking-tight">Welcome back</h1>
        <p className="text-muted-foreground mt-2 text-sm">Track your saved properties and share requirements with Tharlo advisors.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <MetricCard
          title="Saved Properties"
          value={likesCount}
          hint="Properties you've hearted"
          cta="View all"
          onClick={() => onNav("saved")}
          testid="buyer-metric-saved"
        />
        <MetricCard
          title="Callback Requested"
          value={cbCount}
          hint="Advisor will reach out soon"
          cta="View list"
          onClick={() => onNav("saved")}
          testid="buyer-metric-callback"
        />
        <MetricCard
          title="Requirements Posted"
          value={reqCount}
          hint="Sent privately to Tharlo advisors"
          cta="Post new"
          onClick={openRequirement}
          testid="buyer-metric-req"
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <ActionCard title="Browse Verified Properties" text="15+ premium listings across Rewa" cta="Start Browsing" href="/properties" />
        <ActionCard title="Legal Services" text="Title verification, sale deed & registration support" cta="Explore Legal" href="/legal" />
      </div>
    </div>
  );
}

function MetricCard({ title, value, hint, cta, onClick, testid }) {
  return (
    <div className="bg-white border border-border p-5" data-testid={testid}>
      <div className="text-xs uppercase tracking-widest text-muted-foreground">{title}</div>
      <div className="font-display text-4xl text-ink mt-2">{value}</div>
      <div className="text-xs text-muted-foreground mt-1">{hint}</div>
      <button onClick={onClick} className="mt-4 text-gold text-sm font-semibold hover:underline">{cta} →</button>
    </div>
  );
}

function ActionCard({ title, text, cta, href }) {
  return (
    <div className="bg-ink text-white p-6">
      <h4 className="font-display text-lg">{title}</h4>
      <p className="text-white/70 text-sm mt-1">{text}</p>
      <Link to={href} className="mt-3 inline-block text-gold text-sm font-semibold hover:underline">{cta} →</Link>
    </div>
  );
}

/* ============ Saved ============ */
function SavedProperties() {
  const [items, setItems] = useState(null);
  const [cbIds, setCbIds] = useState([]);
  const refresh = async () => {
    const r = await api.get("/my/likes");
    setItems(r.data.items);
    setCbIds(r.data.callback_property_ids || []);
  };
  useEffect(() => { refresh(); }, []);
  if (items === null) return <div className="text-sm text-muted-foreground">Loading...</div>;

  const cbSet = new Set(cbIds);
  const callbackItems = items.filter((p) => cbSet.has(p.id));
  const savedOnlyItems = items.filter((p) => !cbSet.has(p.id));

  return (
    <div className="space-y-10" data-testid="buyer-saved-section">
      <div>
        <h2 className="font-display text-3xl text-ink tracking-tight">Saved Properties</h2>
        <p className="text-sm text-muted-foreground mt-2">Properties you&apos;ve hearted or requested a callback on. Tap the heart on any card to remove it from saved.</p>
      </div>

      {items.length === 0 && (
        <div className="bg-white border border-border p-12 text-center">
          <Heart className="w-8 h-8 text-gold mx-auto mb-3" />
          <h3 className="font-display text-xl text-ink mb-2">No saved properties yet</h3>
          <Link to="/properties" className="btn-primary mt-4 inline-block">Browse Properties</Link>
        </div>
      )}

      {callbackItems.length > 0 && (
        <section data-testid="buyer-saved-callback-group">
          <div className="flex items-center gap-2 mb-3 pb-2 border-b border-border">
            <span className="w-2 h-2 rounded-full bg-gold inline-block" />
            <h3 className="font-display text-xl text-ink">Callback Requested <span className="text-sm text-muted-foreground font-normal">· {callbackItems.length}</span></h3>
          </div>
          <p className="text-xs text-muted-foreground mb-4">Tharlo advisors will contact you shortly about these properties.</p>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
            {callbackItems.map((p, i) => <PropertyCard key={p.id} p={p} index={i} liked={true} onLikeChange={(_id, liked) => { if (!liked) refresh(); }} />)}
          </div>
        </section>
      )}

      {savedOnlyItems.length > 0 && (
        <section data-testid="buyer-saved-only-group">
          <div className="flex items-center gap-2 mb-3 pb-2 border-b border-border">
            <span className="w-2 h-2 rounded-full bg-ink inline-block" />
            <h3 className="font-display text-xl text-ink">Just Saved <span className="text-sm text-muted-foreground font-normal">· {savedOnlyItems.length}</span></h3>
          </div>
          <p className="text-xs text-muted-foreground mb-4">Open any of these and tap &quot;Request Callback&quot; to hear from an advisor.</p>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
            {savedOnlyItems.map((p, i) => <PropertyCard key={p.id} p={p} index={i} liked={true} onLikeChange={(_id, liked) => { if (!liked) refresh(); }} />)}
          </div>
        </section>
      )}
    </div>
  );
}

/* ============ My Requirements ============ */
function MyRequirements({ openPost }) {
  const [items, setItems] = useState(null);
  useEffect(() => { api.get("/my/requirements").then((r) => setItems(r.data.items)); }, []);
  if (items === null) return <div className="text-sm text-muted-foreground">Loading...</div>;

  const fmtBudget = (min, max) => {
    if (!min && !max) return "—";
    if (min && max) return `${formatINR(min)} – ${formatINR(max)}`;
    if (min) return `${formatINR(min)}+`;
    return `up to ${formatINR(max)}`;
  };

  return (
    <div className="space-y-6" data-testid="buyer-requirements-section">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
        <div>
          <h2 className="font-display text-3xl text-ink tracking-tight">My Requirements</h2>
          <p className="text-sm text-muted-foreground mt-2">Requirements you've shared with Tharlo. Our advisors match and reach out.</p>
        </div>
        <button onClick={openPost} data-testid="buyer-post-requirement" className="btn-gold !py-2.5">
          <Sparkles className="w-4 h-4" /> Post New Requirement
        </button>
      </div>
      {items.length === 0 ? (
        <div className="bg-white border border-border p-12 text-center">
          <ClipboardList className="w-8 h-8 text-gold mx-auto mb-3" />
          <h3 className="font-display text-xl text-ink mb-2">No requirements yet</h3>
          <button onClick={openPost} className="btn-primary mt-4">Post Your First Requirement</button>
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((r) => (
            <div key={r.id} className="bg-white border border-border p-5" data-testid={`buyer-req-${r.id}`}>
              <div className="flex flex-wrap items-center gap-2 mb-2">
                <span className="trust-badge bg-ink text-white border-ink">{r.property_type}</span>
                {r.listing_type !== "any" && <span className="trust-badge">{r.listing_type}</span>}
                <span className={`trust-badge ${r.status === "new" ? "bg-yellow-100 text-yellow-700 border-yellow-300" : "bg-green-100 text-green-700 border-green-300"}`}>{r.status}</span>
                <span className="text-xs text-muted-foreground ml-auto">{r.created_at?.slice(0, 10)}</span>
              </div>
              {r.name && <div className="text-xs text-muted-foreground mb-2">Posted as <strong className="text-ink">{r.name}</strong></div>}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
                <div><span className="text-muted-foreground">Location: </span><strong>{r.locality ? `${r.locality}, ` : ""}{r.city}</strong></div>
                <div><span className="text-muted-foreground">Budget: </span><strong>{fmtBudget(r.budget_min, r.budget_max)}</strong></div>
                <div><span className="text-muted-foreground">Timeline: </span><strong>{r.timeline || "—"}</strong></div>
              </div>
              {r.bhk && <div className="mt-2 text-sm"><span className="text-muted-foreground">BHK: </span><strong>{r.bhk}</strong></div>}
              {r.notes && <div className="mt-3 text-sm text-foreground/80 border-l-2 border-gold pl-3 bg-muted/30 p-3">{r.notes}</div>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ============ My Unlock Requests (paid Trust Score / Fair Price intent) ============ */
function MyUnlockRequests() {
  const [items, setItems] = useState(null);
  useEffect(() => { api.get("/my/unlock-requests").then((r) => setItems(r.data.items)); }, []);
  if (items === null) return <div className="text-sm text-muted-foreground">Loading...</div>;

  const statusStyle = {
    pending: "bg-yellow-100 text-yellow-700 border-yellow-300",
    in_progress: "bg-blue-100 text-blue-700 border-blue-300",
    delivered: "bg-green-100 text-green-700 border-green-300",
    cancelled: "bg-gray-100 text-gray-600 border-gray-300",
  };

  return (
    <div className="space-y-6" data-testid="buyer-unlocks-section">
      <div>
        <h2 className="font-display text-3xl text-ink tracking-tight">Unlock Requests</h2>
        <p className="text-sm text-muted-foreground mt-2">Tharlo Trust Score and Fair Price reports you've asked to unlock. Our advisor will contact you with scope and pricing.</p>
      </div>
      {items.length === 0 ? (
        <div className="bg-white border border-border p-12 text-center">
          <Lock className="w-8 h-8 text-gold mx-auto mb-3" />
          <h3 className="font-display text-xl text-ink mb-2">No unlock requests yet</h3>
          <p className="text-sm text-muted-foreground mb-4">Open any property and tap "Request Unlock" on a Tharlo verified report to see it listed here.</p>
          <Link to="/properties" className="btn-primary">Browse Properties</Link>
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((u) => (
            <div key={u.id} className="bg-white border border-border p-5" data-testid={`buyer-unlock-${u.id}`}>
              <div className="flex flex-wrap items-center gap-2 mb-2">
                <span className="trust-badge bg-gold/10 border-gold/40 text-ink">{u.report_label}</span>
                <span className={`trust-badge ${statusStyle[u.status] || statusStyle.pending}`}>{u.status.replace("_", " ")}</span>
                <span className="text-xs text-muted-foreground ml-auto">{u.created_at?.slice(0, 10)}</span>
              </div>
              <Link to={`/property/${u.property_slug || u.property_id}`} className="text-sm text-ink font-semibold hover:text-gold">
                {u.property_title}
              </Link>
              {u.status === "pending" && <p className="mt-2 text-xs text-muted-foreground">Awaiting Tharlo advisor response. Typically within 1 business day.</p>}
              {u.status === "in_progress" && <p className="mt-2 text-xs text-blue-700">Tharlo team is working on your report.</p>}
              {u.status === "delivered" && <p className="mt-2 text-xs text-green-700">Report delivered. Check your email or contact Tharlo advisor for details.</p>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ============ Profile (buyer — name, email, phone only) ============ */
function BuyerProfile() {
  const { refresh } = useAuth();
  const [f, setF] = useState(null);
  const [saving, setSaving] = useState(false);
  const input = "w-full bg-white border border-border focus:border-ink outline-none px-4 py-3 text-sm";

  useEffect(() => {
    api.get("/me/profile").then((r) => setF({
      full_name: r.data.full_name || r.data.name || "",
      email: r.data.email && !r.data.email.endsWith("@phone.tharlo") ? r.data.email : "",
      mobile: r.data.mobile || r.data.phone || "",
      user_type: r.data.user_type || "buyer",
    }));
  }, []);

  if (!f) return <div className="text-sm text-muted-foreground">Loading...</div>;

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.patch("/me/profile", { full_name: f.full_name, email: f.email || null, mobile: f.mobile });
      if (f.user_type) await api.post("/me/user-type", { user_type: f.user_type });
      await refresh();
      toast.success("Profile updated");
    } catch { toast.error("Update failed"); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-6" data-testid="buyer-profile-section">
      <div>
        <h2 className="font-display text-3xl text-ink tracking-tight">Profile</h2>
        <p className="text-sm text-muted-foreground mt-2">Keep your contact details up to date so Tharlo can reach you.</p>
      </div>
      <form onSubmit={save} className="bg-white border border-border p-6 space-y-3 max-w-xl">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Full Name</div>
          <input data-testid="buyer-name" required className={input} value={f.full_name} onChange={(e) => setF({ ...f, full_name: e.target.value })} />
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Mobile Number</div>
          <input data-testid="buyer-mobile" required className={input} value={f.mobile} onChange={(e) => setF({ ...f, mobile: e.target.value })} />
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Email</div>
          <input data-testid="buyer-email" type="email" className={input} value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} />
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Account Type</div>
          <select data-testid="buyer-user-type" className={input} value={f.user_type} onChange={(e) => setF({ ...f, user_type: e.target.value })}>
            <option value="buyer">Buyer only</option>
            <option value="both">Both (Buyer + Seller)</option>
            <option value="seller">Seller only</option>
          </select>
          <div className="text-[11px] text-muted-foreground mt-1">Switch to "Both" or "Seller" to also access the seller dashboard.</div>
        </div>
        <button data-testid="buyer-profile-save" disabled={saving} className="btn-primary w-full">{saving ? "Saving..." : "Save Changes"}</button>
      </form>
    </div>
  );
}

/* ============ Subscription (buyer plans, placeholder) ============ */
function BuyerSubscription() {
  return (
    <div className="space-y-6" data-testid="buyer-subscription-section">
      <div>
        <h2 className="font-display text-3xl text-ink tracking-tight">Buyer Plans</h2>
        <p className="text-sm text-muted-foreground mt-2">Premium buyer support launching soon. Free access to all listings today.</p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {BUYER_PLANS.map((plan) => <PlanCard key={plan.title} {...plan} />)}
      </div>
    </div>
  );
}

function PlanCard({ title, price, period, features, active, disabled }) {
  return (
    <div className={`p-6 border ${active ? "bg-ink text-white border-ink" : "bg-white border-border"} ${disabled ? "opacity-70" : ""}`}>
      <div className="font-display text-2xl">{title}</div>
      <div className={`text-3xl font-bold mt-2 ${active ? "text-gold" : "text-ink"}`}>{price}</div>
      <div className={`text-xs uppercase tracking-widest mt-1 ${active ? "text-white/60" : "text-muted-foreground"}`}>{period}</div>
      <ul className={`mt-5 space-y-2 text-sm ${active ? "text-white/85" : "text-foreground/80"}`}>
        {features.map((ft) => <li key={ft} className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0 text-gold" /> {ft}</li>)}
      </ul>
      {active && <div className="mt-5 text-xs uppercase tracking-widest text-gold">Active</div>}
      {disabled && <div className="mt-5 text-xs uppercase tracking-widest text-muted-foreground">Notify me when live</div>}
    </div>
  );
}

/* ============ Support ============ */
function SupportSection() {
  const [form, setForm] = useState({ subject: "", message: "" });
  const [busy, setBusy] = useState(false);
  const [lastTag, setLastTag] = useState(null);
  const input = "w-full bg-white border border-border focus:border-ink outline-none px-4 py-3 text-sm";
  const submit = async (e) => {
    e.preventDefault();
    if (!form.subject || !form.message) { toast.error("Subject & message required"); return; }
    setBusy(true);
    try {
      const r = await api.post("/support", form);
      const tag = r?.data?.tag;
      setLastTag(tag || null);
      toast.success(tag ? `Ticket ${tag} created. We'll respond within 24 hours.` : "Ticket sent to Tharlo.");
      setForm({ subject: "", message: "" });
    } catch (err) {
      logDev.error("support submit failed:", err);
      toast.error("Failed to submit ticket. Please try again.");
    }
    finally { setBusy(false); }
  };
  const copyTag = () => {
    if (!lastTag) return;
    navigator.clipboard?.writeText(lastTag).then(
      () => toast.success("Ticket ID copied"),
      () => toast.error("Could not copy — please note the ID manually"),
    );
  };
  return (
    <div className="space-y-6" data-testid="buyer-support-section">
      <div>
        <h2 className="font-display text-3xl text-ink tracking-tight">Support</h2>
        <p className="text-sm text-muted-foreground mt-2">Need help finding the right property? Send us a message.</p>
      </div>
      {lastTag && (
        <div className="bg-green-50 border border-green-300 p-5 space-y-2" data-testid="buyer-support-tag-block">
          <div className="flex items-center gap-2 text-green-800">
            <CheckCircle2 className="w-5 h-5" />
            <span className="font-semibold">Ticket received — Tharlo will get back to you within 24 hours.</span>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <span className="text-xs uppercase tracking-widest text-green-800/80">Your ticket ID</span>
            <code className="text-base font-mono font-bold text-ink bg-white border border-green-300 px-3 py-1" data-testid="buyer-support-tag">{lastTag}</code>
            <button type="button" onClick={copyTag} data-testid="buyer-support-tag-copy" className="text-xs text-gold hover:underline font-semibold">Copy</button>
          </div>
          <p className="text-xs text-green-800/80">Please quote this ID in any email or WhatsApp reply so we can pull up your ticket instantly.</p>
        </div>
      )}
      <form onSubmit={submit} className="bg-white border border-border p-6 space-y-3 max-w-2xl">
        <input data-testid="buyer-support-subject" required className={input} placeholder="Subject" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} />
        <textarea data-testid="buyer-support-message" required className={input} rows={6} placeholder="How can we help?" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} />
        <button data-testid="buyer-support-submit" disabled={busy} className="btn-gold w-full">{busy ? "Sending..." : "Send Ticket"}</button>
      </form>
    </div>
  );
}
